
<template>
  <v-skeleton-loader
    v-if="loading"
    class="m-auto"
    type="table"
    transition="scale-transition"
  />
  <TableRemote
    v-else
    isSearchable
    :headers="tableHeaders"
    hideDefaultFooter
    :isInfinitScroll="true"
    :iconHeader="hasPermission('analyses.groups.add') ? 'add' : null"
    :items="displayedGroups"
    :itemActions="actions"
    :title="$t('analyses.groups.pageName')"
    :pagination="pagination"
    :isCustomIcon="true"
    :centerColumns="['action']"
    :notSortableColumns="['action', 'analysisTypes']"
    :sortableColumns="['name', 'ERPCode']"
    @iconAction="onClickAction"
    @sendQueryParams="fetchQuery"
  >
    <template #item.analysisTypes="{ item }">
      <List
        :items="item.analysisTypes"
        :verticalList="true"
      />
    </template>
    <template #withInfiniteScroll>
      <div v-intersect="endIntersect" />
    </template>
  </TableRemote>
</template>


<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { cloneDeep } from 'lodash';
import AnalysisGroupForm from '@/components/forms/AnalysisGroupForm.vue';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm';
import List from '@/components/shared/content-views/slots/List';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import perpage from '@/constants/pagination';

export default {
    components: {
        TableRemote,
        List,
    },
    data() {
        return {
            analysisGroups: [],
            loading: false,
            pagination: {},
            queryParams: {
                perpage,
                page: 1,
                order: 'name_asc',
            },
        };
    },
    computed: {
        ...mapGetters([
            'userLang',
            'hasPermission',
            'teamMainLang',
            'teamLangs',
        ]),
        tableHeaders() {
            return [
                {
                    text: this.$t('forms.labels.name'),
                    value: 'name',
                    width: '400px',
                },
                {
                    text: this.$t('forms.labels.ERPCode'),
                    value: 'ERPCode',
                    width: '200px',

                },
                {
                    text: this.$t('forms.labels.analysisTypes'),
                    value: 'analysisTypes',
                },
            ];
        },
        displayedGroups() {
            return this.analysisGroups.map(group => ({
                ...group,
                id: group.id,
                name: !group.name ? '' : this.getName(group),
                analysisTypes: group.types.map(type => type.name[this.userLang] || type.name[this.teamMainLang]),
            }));
        },
        actions() {
            const actions = [];

            if (this.hasPermission('analyses.groups.edit')) {
                actions.push({
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                });
            }

            if (this.hasPermission('analyses.groups.remove')) {
                actions.push({
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                });
            }

            return actions;
        },
    },
    async created() {
        this.loading = true;
        await this.getGroups();
        this.loading = false;
    },
    methods: {
        getName(group) {
            return group.name[this.userLang] && group.name[this.userLang].length
                ? group.name[this.userLang] : group.name[this.teamMainLang];
        },
        async getGroups() {
            const { data } = await this.$rq.getAnalysisGroups(this.queryParams);
            this.pagination = data.pagination || {};
            this.analysisGroups = [...this.analysisGroups, ...data.groups];
        },
        onClickAdd(event) {
            if (event === 'add') {
                this.$root.$emit('showModal', {
                    title: this.$t('analyses.groups.add'),
                    component: Vue.component('AnalysisGroupForm', AnalysisGroupForm),
                    showLangs: true,
                    isForm: true,
                    onConfirm: requestPayload => {
                        this.addGroup(requestPayload);
                    },
                });
            }
        },
        async onClickAction(item, action) {
            if (!action && item === 'add') {
                this.onClickAdd(item);
            }
            if (action.mode === 'edit') {
                const { data } = await this.$rq.getAnalysisGroup(item.id, { showallattributes: !!this.hasPermission('showAllAttributes') });
                // get id from data (object)
                const types = data.types.map(type => type.id);
                // Replace type value into the object
                data.types = types;
                this.$root.$emit('showModal', {
                    title: this.$t('analyses.groups.update'),
                    component: Vue.component('AnalysisGroupForm', AnalysisGroupForm),
                    showLangs: true,
                    isForm: true,
                    payload: { group: cloneDeep(data), isEdition: true },
                    onConfirm: requestPayload => {
                        this.updateGroup(item.id, requestPayload);
                    },
                });
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('analyses.groups.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('analyses.groups.confirmRemove') },
                    onConfirm: () => {
                        this.removeGroup(item);
                    },
                    isForm: false,
                });
            }
        },
        preparePayload(payload) {
            const { types, ...requestPayload } = payload;
            // Rename 'types` to 'analysisTypes'
            requestPayload.analysisTypes = types;
            return requestPayload;
        },
        async addGroup(payload) {
            const requestPayload = this.preparePayload(payload);
            const request = this.$utils.removeEmptyNullOrUndefined(requestPayload, true);

            const { data } = await this.$rq.addAnalysisGroup(request);
            this.$toasted.global.toast_success({
                message: this.$t('analyses.groups.added', { name: data.name[this.userLang] || data.name[this.teamMainLang] }),
            });
            this.analysisGroups = [];
            this.getGroups();
        },
        async updateGroup(groupId, payload) {
            const requestPayload = this.preparePayload(payload);
            const { data } = await this.$rq.updateAnalysisGroup(groupId, requestPayload);
            this.$toasted.global.toast_success({
                message: this.$t('analyses.groups.updated', { name: data.name }),
            });
            this.analysisGroups = [];
            this.getGroups();
        },
        async removeGroup(group) {
            await this.$rq.removeAnalysisGroup(group.id);
            this.$toasted.global.toast_success({
                message: this.$t('analyses.groups.removed', { name: group.name }),
            });
            this.analysisGroups = [];
            this.getGroups();
        },
        async fetchQuery(params) {
            this.queryParams = {
                ...this.queryParams,
                ...params,
                search: params?.search || null,
                page: 1,
                order: params.order,
            };
            this.pagination = {};
            this.analysisGroups = [];
            await this.getGroups();
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getGroups();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },

    },
};
</script>
